<script setup lang="ts">
// types
import type { SectionGradeTypeProps } from '@ts-sections/section-grade-type/interfaces'
// hooks
import { useSectionGradeType } from '@hooks/sections/section-grade-type.hook'
// helpers
import { STRAPI_IMAGE_SIZES } from '@helpers/strapi-media'
// components
import BlockGradeTypeCard from '@Blocks/BlockGradeTypeCard.vue'
import { AppImage } from '@UI/Images/AppImage'
import { SectionLayout } from '@UI/Layouts/SectionLayout'

defineOptions({
  name: 'SectionGradeType',
})

const props = defineProps<{
  section: SectionGradeTypeProps
}>()

const {
  refTitle,
  refBackgroundColor,
  refTrack,
  refSticky,
  refActiveIndex,
  getGradeColor,
  getImageAlt,
} = useSectionGradeType({
  section: () => props.section,
})
</script>

<template>
  <SectionLayout
    class="section-grade-type"
    :data-component="section.__component"
    :title="refTitle"
    :title-tag="section.layout?.titleTag"
    :color="refBackgroundColor"
    :space="section.layout?.space"
  >
    <div
      ref="refTrack"
      class="section-grade-type__track"
    >
      <div
        ref="refSticky"
        class="section-grade-type__sticky"
      >
        <div class="section-grade-type__grid">
          <ol class="section-grade-type__list">
            <li
              v-for="(grade, index) in section.grades"
              :key="grade.id ?? index"
              class="section-grade-type__item"
              :class="`section-grade-type__item--${getGradeColor(index)}`"
            >
              <BlockGradeTypeCard
                class="section-grade-type__card"
                :block="grade"
                :color="getGradeColor(index)"
                :is-open="index === refActiveIndex"
              />
              <div class="section-grade-type__media section-grade-type__media--item">
                <AppImage
                  v-if="grade.image"
                  class="section-grade-type__photo"
                  :media="grade.image"
                  :sizes="STRAPI_IMAGE_SIZES"
                  :alt="getImageAlt(grade)"
                />
              </div>
            </li>
          </ol>
          <div
            class="section-grade-type__media-stack"
            aria-hidden="true"
          >
            <div
              v-for="(grade, index) in section.grades"
              :key="grade.id ?? index"
              class="section-grade-type__media"
              :class="[
                `section-grade-type__item--${getGradeColor(index)}`,
                { 'section-grade-type__media--active': index === refActiveIndex },
              ]"
            >
              <AppImage
                v-if="grade.image"
                class="section-grade-type__photo"
                :media="grade.image"
                :sizes="STRAPI_IMAGE_SIZES"
                alt=""
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </SectionLayout>
</template>

<style scoped lang="scss" src="@scss/components/Sections/SectionGradeType.scss" />
